import { Mui, MuiIcons, MuiStyles } from "@osu/react-ui";
import useRoleAuthentication from "../../Authentication/hooks/useRoleAuthentication";
import LoadingSpinner from "../../Common/components/LoadingSpinner";
import { useGetApplicationDetailsQueryState, useGetScholarsApplicationDetailsQueryState } from "../../services/graphql/api";
import { PROGRAM_COLOR_MAP } from "../../util/constants";
import { getApplDetailParams } from "../../util/functions";
import { subProgramColor } from "../../util/scholars/subProgramColor";
import useChipSubProgram from "../../util/scholars/useChipSubProgram";
import { getScholarsApplDetailParams } from "./ScholarsApplicant";
import ActionsButtonAndPopupMenu from "./Actions/ButtonAndPopupMenu";
import useScholarsActionMenuItems from "./Actions/useScholarsActionMenuItems";

// has503 — the Scholars applicant details page uses a separate query + URL shape
// (/scholars/applicant/:id/:term). Detect it so the app bar reads the correct cache.
function isScholarsApplicantRoute() {
    return window.location.pathname.includes("/scholars/applicant");
}

const styles = {
    toolbar: {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
    },
    chipLabel: {
        alignItems: "center",
        display: "flex",
    },
    midReviewLoading: {
        left: "0px",
        position: "absolute",
        top: "-5px",
        width: "100%",
    }
};

function ApplicantDetailsPageAppBar() {
    const isOnApplicantDetailsPage = window.location.href.includes("/applicant") || window.location.href.includes("/view");
    if (!isOnApplicantDetailsPage) { return <></>; }
    
    return (
        <>
          <Mui.Divider/>
          <Mui.Toolbar>
            {isScholarsApplicantRoute() ? <ScholarsAppBarContent /> : <ApplicantAppBarContent />}
          </Mui.Toolbar> 
        </>
    );   
}

function ApplicantAppBarContent() {
    const { data, error, isLoading, isFetching } = useGetApplicationDetailsQueryState(getApplDetailParams());
    if (error) { return <Mui.Typography>Error: {error.message}</Mui.Typography>; }
    const opacity = isFetching ? "0.5" : "1.0";
    
    return (
        <>
            { !isLoading && isFetching && <Mui.Box sx={styles.midReviewLoading}><Mui.LinearProgress /></Mui.Box> }
            <Mui.Toolbar style={{ opacity }} sx={styles.toolbar}>
                <LoadingSpinner isFetching={isLoading} count={1} height={60} width="100%" />
            
                <AppBarDetails data={data?.details.appBar} />

                { data && <ActionsButtonAndPopupMenu studentData={data?.status} /> }
            </Mui.Toolbar>
        </>
    );
}

// has503 — Scholars app bar: reads the Scholars details cache with the Scholars URL params.
// Omits the Honors review Actions menu (Scholars actions live in the page's Review and Decision section).
function ScholarsAppBarContent() {
    const { data, error, isLoading, isFetching } = useGetScholarsApplicationDetailsQueryState(getScholarsApplDetailParams());
    // has540 — resolve the scholars program that should color/label the chip (URL ?sp wins, else PM identity).
    const subProgram = useChipSubProgram();
    if (error) { return <Mui.Typography>Error: {error.message}</Mui.Typography>; }
    const opacity = isFetching ? "0.5" : "1.0";

    return (
        <>
            { !isLoading && isFetching && <Mui.Box sx={styles.midReviewLoading}><Mui.LinearProgress /></Mui.Box> }
            <Mui.Toolbar style={{ opacity }} sx={styles.toolbar}>
                <LoadingSpinner isFetching={isLoading} count={1} height={60} width="100%" />
                <AppBarDetails data={data?.details.appBar} subProgram={subProgram} />

                { data && <ActionsButtonAndPopupMenu useMenuItems={useScholarsActionMenuItems} hideOnViewPath={false} /> }
            </Mui.Toolbar>
        </>
    );
}

function AppBarDetails({ data, subProgram }) {
    if (!data) { return <></>; }
    const fullName = `${data.firstName ?? "-"} ${data.lastName ?? "-"}`;
    // has540 — when a scholars program is in context (Scholars route only), the chip takes that scholars program's
    // name + accent color (the SAME deterministic color the queue cards use). Otherwise it falls back to
    // the program-level color/name (the default lavender "Scholars", or the Honors color on that route).
    const color = PROGRAM_COLOR_MAP[data.programId];
    const chipLabel = data.programName;
    const dob = data.dateOfBirth ? new Date(data.dateOfBirth).toLocaleDateString() : "";
    
    const subProgramChipColor = subProgramColor(subProgram?.subProgramId);
    const subProgramChipLabel = subProgram?.label;

    return (
        <Mui.Box style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between'}}>
            <Mui.Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                <Mui.Typography component="span" variant="h5" tabIndex="0">{fullName}</Mui.Typography>
                <ProgramChip color={color} programName={chipLabel} />
                {subProgramChipLabel && <ProgramChip color={subProgramChipColor} programName={subProgramChipLabel} /> }
                <ThirdReviewerChip color={color} is3rdReviewer={data.is3rdReviewer}/>
                <PriorityChip priority={data.priority} />
            </Mui.Box>
            <Mui.Box>
                <ExtraSubfield label="ID"     value={data.applicantEmployeeId} divider={false} />
                <ExtraSubfield label="DOB"    value={dob}                      shouldShow={!!dob} />
                <ExtraSubfield label="Name.#" value={data.campusId} />
            </Mui.Box>
        </Mui.Box>  
    );
}

function ExtraSubfield({ label, value, divider = true, shouldShow = true }) {
    if (!shouldShow) { return; }
    const inlineBlock = { "display": "inline-block" };
    
    return (
        <>
            { divider && <Mui.Typography color="secondary" sx={inlineBlock}>&nbsp;|&nbsp;</Mui.Typography> }
            <Mui.Typography color="secondary" sx={inlineBlock}>{label}:&nbsp;</Mui.Typography>
            <Mui.Typography aria-description={label} color="secondary" sx={inlineBlock} tabIndex="0">{value}</Mui.Typography>
        </>
    );
}

function ProgramChip({ color, programName }) {
    return (
        <Mui.Chip style={{ marginLeft: "1rem", border: `1px solid ${color}`, }}
            label={
                <Mui.Typography sx={styles.chipLabel} tabIndex="0">
                    <MuiIcons.FiberManualRecord style={{ color, verticalAlign: "middle", }} />
                    {programName}
                </Mui.Typography>
            }
            variant="outlined"
        />
    );
}

function ThirdReviewerChip({ color, is3rdReviewer }) {
    if (!is3rdReviewer) { return <></>; }
    return (
        <Mui.Chip className={'margin-left-2'} 
            style={{ border: `1px solid ${color}`, backgroundColor: color }}
            label={<Mui.Typography>Third Review</Mui.Typography>}
        />
    )
}

const createClasses = (theme) => {
    return {
      chipPriority: {
        backgroundColor: theme.palette.primary.main,
        border: `1px solid ${theme.palette.primary.main}`,
        marginLeft: "1rem",
      },
    };
};

function PriorityChip({ priority }) {
    const classes = createClasses(MuiStyles.useTheme());
    const authDetails = useRoleAuthentication();

    if (!priority || !authDetails.isAnyAdmin || !authDetails.isHonorsSeasonalManager || !authDetails.isHonorsProgramCoordinator) { return <></>; }

    return (
        <Mui.Chip
            className={classes.chipPriority}
            label={
                <Mui.Typography aria-label="Is a priority" style={{ color: 'white' }} sx={styles.chipLabel} tabIndex="0">Priority</Mui.Typography>
            }
            variant="outlined"
        />
    );
}

export default ApplicantDetailsPageAppBar;
